import { Barcode, POSTicketEposPrinter } from '@pos/interfaces/pos-v2';
import { forEach } from 'lodash';
import { i18n } from '@/main';
import { useCommonHelper } from '@core/helpers/common-helper';

type onReceiveResponse = {
  success: boolean;
  code: string;
  status: number;
  battery: number;
  printjobid: string;
};

type createDeviceResult =
  | 'OK'
  | 'DEVICE_NOT_FOUND'
  | 'DEVICE_IN_USE'
  | 'DEVICE_OPEN_ERROR'
  | 'DEVICE_TYPE_INVALID'
  | 'PARAM_ERROR'
  | 'SYSTEM_ERROR';

type connectResult = 'OK' | 'SSL_CONNECT_OK' | 'ERROR_TIMEOUT' | 'ERROR_PARAMETER';

export type printType = 'ticket' | 'badge' | 'drawer';

export default class EpsonEposPrinterService {
  protected readonly epson: any;
  protected printer: any;
  protected port: string = '8043';
  constructor() {
    if (typeof window !== 'undefined' && window.epson) {
      this.epson = new window.epson.ePOSDevice();
    }
    this.port = `${process.env.VUE_APP_EPOS_PRINTER_PORT}` || '8043'; // use 8008 for localhost
  }

  public isAvailable(): boolean {
    return !!this.epson;
  }

  public isConnected(): boolean {
    return !!this.epson.isConnected();
  }

  private connect(ipAddress: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnected()) {
        // Delete the device and disconnect if it is already connected
        this.epson.deleteDevice(this.printer, () => {
          this.epson.disconnect();
        });
      }

      this.epson.connect(ipAddress, this.port, (result: connectResult) => {
        if (result === 'OK' || result === 'SSL_CONNECT_OK') {
          resolve();
        } else {
          reject(new Error(`Error connecting to printer: ${result}`));
        }
      });
    });
  }

  private createDevice(): Promise<any> {
    return new Promise((resolve, reject) => {
      const options = { crypto: false, buffer: false };
      this.epson.createDevice(
        'bc_printer',
        this.epson.DEVICE_TYPE_PRINTER,
        options,
        (printer: any, result: createDeviceResult) => {
          if (result === 'OK') {
            this.printer = printer;
            resolve(printer);
          } else {
            reject(new Error(`Error creating device: ${result}`));
          }
        },
      );
    });
  }

  private printTicket(ticketData: POSTicketEposPrinter): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.printer) {
        return reject(new Error('Printer not initialized'));
      }

      this.printer.onreceive = (response: onReceiveResponse) => {
        if (response.success) {
          this.epson.deleteDevice(this.printer, () => {
            this.epson.disconnect();
            if (this.isConnected()) {
              reject(new Error('Printer still connected'));
            } else {
              resolve();
            }
          });
        } else {
          reject(new Error(`Printing error: ${response.code}`));
        }
      };

      // Width of the paper 80mm = 48 characters
      const PAPER_WIDTH = 48;

      // Column positions
      const POS_ARTICLE = 0; // Initial position of "Article"
      const POS_QUANTITY = 30; // Position where the quantity starts
      const POS_AMOUNT = 40; // Position where the amount starts

      // Max of characters by column
      const MAX_ARTICLE_LENGTH = POS_QUANTITY - POS_ARTICLE - 2; // Leave 2 spaces of margin
      const MAX_AMOUNT_LENGTH = 10; // For big numbers (until 9999999.99)

      // Function to create a line that is the width of the paper.
      const getDivider = () => '-'.repeat(PAPER_WIDTH) + '\n';

      this.printer.addTextLang('mul');
      this.printer.addTextAlign(this.printer.ALIGN_CENTER);
      this.printer.addLogo(32, 32);
      this.printer.addText('\n');
      this.printer.addText(`${i18n.t('global.ticket_epos_printer_ticket_title')}\n\n`);

      // Print the code as a bar code
      // this.printer.addBarcode(
      //   '{A' + ticketData.code,
      //   this.printer.BARCODE_CODE128,
      //   this.printer.HRI_NONE,
      //   this.printer.FONT_A,
      //   3,
      //   50,
      // );
      // this.printer.addText('\n');

      this.printer.addFeed(1);

      // Function for creating lines with control of max length
      const createLine = (article: string, quantity: string, amount: string): string => {
        // Initialize the line with spaces
        let line = ' '.repeat(PAPER_WIDTH);

        // Article (aligned to the left and truncated if necessary)
        const safeArticle =
          article.length > MAX_ARTICLE_LENGTH
            ? article.substring(0, MAX_ARTICLE_LENGTH)
            : article.padEnd(MAX_ARTICLE_LENGTH, ' ');
        line = safeArticle + line.substring(safeArticle.length);

        // Quantity (centered in its position)
        const safeQuantity = POS_QUANTITY + Math.floor((5 - quantity.length) / 2);
        line =
          line.substring(0, safeQuantity) +
          quantity +
          line.substring(safeQuantity + quantity.length);

        // Amount (aligned to the left and truncated if necessary)
        const safeAmount =
          amount.length > MAX_AMOUNT_LENGTH
            ? amount.substring(amount.length - MAX_AMOUNT_LENGTH)
            : amount;

        // Calculate the position to insert the amount
        const amountPosition = POS_AMOUNT + (MAX_AMOUNT_LENGTH - safeAmount.length);

        // Check that the amount position is valid
        const finalAmountPosition = Math.max(
          POS_QUANTITY + quantity.length,
          Math.min(PAPER_WIDTH - safeAmount.length, amountPosition),
        );

        // Add the amount to the line
        line =
          line.substring(0, finalAmountPosition) +
          safeAmount +
          line.substring(finalAmountPosition + safeAmount.length);

        return line.substring(0, PAPER_WIDTH);
      };

      // Headers positioned
      const headerLine = createLine(
        i18n.t('global.ticket_epos_printer_article_header').toString(),
        i18n.t('global.ticket_epos_printer_quantity_header').toString(),
        i18n.t('global.ticket_epos_printer_amount_header').toString(),
      );
      this.printer.addText(headerLine + '\n');
      this.printer.addText(getDivider());

      // List of articles
      forEach(ticketData.products, item => {
        // Format the quantity and amount
        const quantityStr = item.quantity.toString();
        const amountStr = useCommonHelper().formatPrice(item.total);

        // Create the line with all the values positioned
        const line = createLine(item.name, quantityStr, amountStr);
        this.printer.addText(line + '\n');
      });

      this.printer.addText(getDivider());

      // Total purchase (aligned to the right)
      this.printer.addTextAlign(this.printer.ALIGN_RIGHT);
      this.printer.addText(
        `${i18n.t('global.ticket_epos_printer_total_label')}: ${useCommonHelper().formatPrice(
          ticketData.total,
        )}\n`,
      );
      this.printer.addFeed(1);

      // Info about the payment and date
      this.printer.addTextAlign(this.printer.ALIGN_LEFT);
      const date = useCommonHelper().formatDateV2(ticketData.date);
      this.printer.addText(`${i18n.t('global.ticket_epos_printer_date_label')}: ${date}\n`);

      // TODO: Add more info about the payment (take into account label for custom payments)

      // End of ticket
      this.printer.addFeed(3);
      this.printer.addCut(this.printer.CUT_FEED);

      // If the user connects a printer with a drawer, open it
      this.printer.addPulse(this.printer.DRAWER_2, this.printer.PULSE_300);
      this.printer.send();
    });
  }

  private printBadge(badges: Barcode[]): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.printer) {
        return reject(new Error('Printer not initialized'));
      }

      this.printer.onreceive = (response: onReceiveResponse) => {
        if (response.success) {
          this.epson.deleteDevice(this.printer, () => {
            this.epson.disconnect();
            if (this.isConnected()) {
              reject(new Error('Printer still connected'));
            } else {
              resolve();
            }
          });
        } else {
          reject(new Error(`Printing error: ${response.code}`));
        }
      };

      this.printer.addTextLang('mul');
      this.printer.addTextAlign(this.printer.ALIGN_CENTER);

      forEach(badges, badge => {
        this.printer.addText(`${badge.quantity} x ${badge.title}\n`);
        this.printer.addFeed(1);

        switch (badge.type) {
          case 'qr':
            this.printer.addSymbol(
              badge.code.toString(),
              this.printer.SYMBOL_QRCODE_MODEL_2,
              this.printer.LEVEL_2,
              8,
              8,
              0,
            );
            break;

          case 'upc_barcode':
            this.printer.addBarcode(
              badge.code.toString(),
              this.printer.BARCODE_UPC_A,
              this.printer.HRI_BELOW,
              this.printer.FONT_A,
              4,
              100,
            );
            break;

          case '128_barcode':
            this.printer.addBarcode(
              '{C' + badge.code,
              this.printer.BARCODE_CODE128,
              this.printer.HRI_BELOW,
              this.printer.FONT_A,
              2,
              100,
            );
            break;
        }

        this.printer.addFeed(3);
        this.printer.addCut(this.printer.CUT_FEED);
      });

      // If the user connects a printer with a drawer, open it
      this.printer.addPulse(this.printer.DRAWER_2, this.printer.PULSE_300);
      this.printer.send();
    });
  }

  private openDrawer(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.printer) {
        return reject(new Error('Printer not initialized'));
      }

      this.printer.onreceive = (response: onReceiveResponse) => {
        if (response.success) {
          this.epson.deleteDevice(this.printer, () => {
            this.epson.disconnect();
            if (this.isConnected()) {
              reject(new Error('Printer still connected'));
            } else {
              resolve();
            }
          });
        } else {
          reject(new Error(`Drawer opening error: ${response.code}`));
        }
      };

      // EPOS printers: Send only the pulse command to open the drawer - no printing
      this.printer.addPulse(this.printer.DRAWER_2, this.printer.PULSE_300);
      this.printer.send();
    });
  }

  public async handlePrint(
    ipAddress: string,
    data: Barcode[] | POSTicketEposPrinter | null,
    type: printType,
  ): Promise<void> {
    await this.connect(ipAddress);
    await this.createDevice();
    if (type === 'badge' && Array.isArray(data)) {
      await this.printBadge(data as Barcode[]);
    } else if (type === 'drawer') {
      await this.openDrawer(); // EPOS: Only pulse, no printing
    } else {
      await this.printTicket(data as POSTicketEposPrinter);
    }
  }
}

export const useEpsonEposPrinterService = (): EpsonEposPrinterService => {
  return new EpsonEposPrinterService();
};
